import React from 'react';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useIsFetching,
  useIsMutating,
} from 'react-query';
import { Button } from 'react-covideo-common';
import { FieldArray, useFormikContext } from 'formik';
import { FiPlus } from 'react-icons/fi';
import { Separator } from '../preview/styles';
import { Flex } from 'lib/components/styles/layout';
import { Search } from 'lib/components';
import { UIKitUpsertForm } from '../form/UIKitUpsertForm';
import { UIKitList } from './UIKitList';
import {
  IUIKitsQueryParams,
  IUIKitsResponse,
} from 'lib/api/superadmin/uikits/useQueryUIKits';
import { INITIAL_UI_KIT_VALUES } from '../../constants';
import { ILandingPageBuilderForm } from '../../types';
import { uiKitsKeys } from 'lib/api/superadmin/uikits/uiKitsKeys';
import { useTheme } from 'styled-components/macro';
import { FormikDropdownOption } from 'lib/components/formik/FormikDropdown';

export const DEFAULT_UI_KITS_SIZE = 5;

interface IUIKitProps {
  count?: number;
  params: IUIKitsQueryParams;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  setSaveAction: React.Dispatch<React.SetStateAction<SaveUIKitAction | null>>;
  saveAction: SaveUIKitAction | null;
  setShowLandingPagePreview: (isCreating: boolean) => void;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<IUIKitsResponse, unknown>>;
  totalCount: number;
  pageTotal: number;
  isLoadingUIKits: boolean;
  allFontFamilyOptions: FormikDropdownOption[];
  setSelectedUIKitIndex: React.Dispatch<React.SetStateAction<number | null>>;
  selectedUIKitIndex: number | null;
  handleGoBack: (remove?: <X = any>(index: number) => X | undefined) => void;
  setIsCreate: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  scrollToTop: () => void;
}

export enum SaveUIKitAction {
  SAVE_AS = 'SAVE AS',
  UPSERT = 'UPSERT',
}

export const UIKit = ({
  params,
  searchQuery,
  setSearchQuery,
  setSaveAction,
  saveAction,
  setShowLandingPagePreview,
  fetchNextPage,
  totalCount,
  pageTotal,
  isLoadingUIKits,
  allFontFamilyOptions,
  setSelectedUIKitIndex,
  setIsCreate,
  setIsUpdate,
  selectedUIKitIndex,
  handleGoBack,
  scrollToTop,
}: IUIKitProps) => {
  const { values } = useFormikContext<ILandingPageBuilderForm>();
  const theme = useTheme();
  const isFetching = useIsFetching(uiKitsKeys.list(params)) > 0;
  const isDuplicating = useIsMutating(uiKitsKeys.duplicate()) > 0;

  const toggleShowSaveUIKitModal = (action: SaveUIKitAction | null) => {
    setSaveAction(action);
  };

  const onUIKitSearchHandler = (value: string) => {
    setSearchQuery(value);
  };

  const handleInsertNewUIKit = (push: <X = any>(obj: X) => void) => {
    if (push) {
      push(INITIAL_UI_KIT_VALUES);
      setSelectedUIKitIndex(values.uiKits.length);
      setShowLandingPagePreview(false);
      setIsCreate(true);
    }
  };

  const handleUpdateUIKit = (id: number | undefined) => {
    if (!id) return;
    const index = values.uiKits?.findIndex(uiKit => uiKit.id === id);
    setSelectedUIKitIndex(index);
    setShowLandingPagePreview(false);
    setIsUpdate(true);
  };

  const disabled = isFetching || isLoadingUIKits || isDuplicating;

  return (
    <FieldArray name='uiKits'>
      {({ push, unshift, replace, remove }) => (
        <div>
          <Flex gap='24px'>
            {selectedUIKitIndex !== null ? (
              <UIKitUpsertForm
                handleGoBack={() => handleGoBack()}
                selectedUIKitIndex={selectedUIKitIndex}
                unshift={unshift}
                replace={replace}
                remove={remove}
                push={push}
                saveAction={saveAction}
                toggleShowSaveUIKitModal={toggleShowSaveUIKitModal}
                allFontFamilyOptions={allFontFamilyOptions}
              />
            ) : (
              <>
                <Button
                  text='Add A New UI Kit'
                  icon={<FiPlus />}
                  variant='secondary'
                  disabled={disabled}
                  onClick={() => handleInsertNewUIKit(push)}
                  style={{
                    padding: '33px 70px',
                    background: theme.colors.secondary.non_alpha,
                  }}
                />
                <Separator />
                <Search
                  onSearch={onUIKitSearchHandler}
                  prevSearch={searchQuery}
                  placeholder='Search for UI Kits...'
                  disabled={disabled}
                />
                <UIKitList
                  uiKits={values?.uiKits}
                  isFetching={isFetching}
                  isLoading={isLoadingUIKits}
                  isDuplicating={isDuplicating}
                  handleUpdateUIKit={handleUpdateUIKit}
                  fetchNextPage={fetchNextPage}
                  totalCount={totalCount}
                  pageTotal={pageTotal}
                  params={params}
                  scrollToTop={scrollToTop}
                  remove={remove}
                  unshift={unshift}
                />
              </>
            )}
          </Flex>
        </div>
      )}
    </FieldArray>
  );
};
